// "Choose a picture" (21-user-settings.md, "Preset pictures"): the 30 pictures that ship with the app
// (/avatars/), loaded only when the dialog opens; the one picked is uploaded and set exactly as an
// own upload.
import type { MatrixClient } from 'matrix-js-sdk';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Picture } from '../blocks/Bits';
import { FormDialog } from '../blocks/Dialog';
import { Icon, icons } from '../blocks/icons';
import { describe, Said } from '../core/errors';
import { currentLanguage, t } from '../i18n';
import { putPicture } from './picture';

interface Preset {
  id: string;
  name: Record<string, string>;
}

export function ChoosePicture({ client, onClose }: { client: MatrixClient; onClose: () => void }) {
  const [list, setList] = useState<Preset[]>([]);
  const [loaded, setLoaded] = useState<Set<string>>(new Set());
  const [picked, setPicked] = useState<number>();
  const [focus, setFocus] = useState(0);
  const grid = useRef<HTMLDivElement>(null);
  useEffect(() => void fetch('/avatars/avatars.json').then((r) => r.json()).then(setList, () => {}), []);
  useEffect(() => void (grid.current?.children[focus] as HTMLElement | undefined)?.focus(), [focus, list.length]);
  // ← → ↑ ↓ move (5 columns, 4 on a phone); Space or Enter picks (the radio's own click).
  const move = (e: KeyboardEvent) => {
    const columns = matchMedia('(max-width: 750px)').matches ? 4 : 5;
    const step = ({ ArrowRight: 1, ArrowLeft: -1, ArrowDown: columns, ArrowUp: -columns } as Record<string, number>)[e.key];
    if (step === undefined) return;
    e.preventDefault();
    setFocus((f) => Math.min(list.length - 1, Math.max(0, f + step)));
  };
  const save = async () => {
    const preset = list[picked!];
    try {
      const file = await (await fetch(`/avatars/${preset.id}.png`)).blob();
      await putPicture(client, file, { type: 'image/png', name: `${preset.id}.png` });
    } catch (e) {
      throw e instanceof Said ? e : new Said(`${t('settings.pictureFailed')} ${describe(e, 'save').text}`);
    }
  };
  return (
    <FormDialog title={t('presets.title')} action={t('presets.use')} busyText={t('presets.saving')} submitDisabled={picked === undefined} onSubmit={save} onClose={onClose}>
      <div ref={grid} role="radiogroup" aria-label={t('presets.pictures')} className="presets" onKeyDown={move}>
        {list.map((p, i) => (
          <button
            key={p.id}
            type="button"
            role="radio"
            aria-checked={picked === i}
            aria-label={p.name[currentLanguage()] ?? p.name.en}
            tabIndex={i === focus ? 0 : -1}
            aria-disabled={!loaded.has(p.id) || undefined}
            data-desyr-preset-avatar={p.id}
            onFocus={() => setFocus(i)}
            onClick={() => loaded.has(p.id) && setPicked(i)}
          >
            <Picture src={`/avatars/${p.id}.png`} alt="" onShown={(shown) => shown && setLoaded((s) => new Set(s).add(p.id))} />
            {picked === i && <Icon svg={icons.check} size={16} />}
          </button>
        ))}
      </div>
    </FormDialog>
  );
}
